import { getOssOriginUrl } from '@/utils'
import { EMPTY_VALUE_PLACEHOLDER, FILE_EXTS, FILE_TYPE } from '@/utils/constant'
import useBasePath from '~/composables/useBasePath'

export const FILE_MATERIAL_KIND = Object.freeze({
  VIDEO: 'video',
  IMAGE: 'image',
  DOCUMENT: 'document',
  OTHER: 'other',
})

const MATERIAL_PRIORITY = Object.freeze({
  [FILE_MATERIAL_KIND.VIDEO]: 0,
  [FILE_MATERIAL_KIND.IMAGE]: 1,
  [FILE_MATERIAL_KIND.DOCUMENT]: 2,
  [FILE_MATERIAL_KIND.OTHER]: 3,
})

const DOCUMENT_FILE_TYPES = new Set([
  FILE_TYPE.WORD,
  FILE_TYPE.EXCEL,
  FILE_TYPE.PPT,
  FILE_TYPE.PDF,
  FILE_TYPE.TXT,
])
const KNOWN_FILE_TYPES = new Set(Object.values(FILE_TYPE))
const FILE_EXTENSION_REGEXP = /^[a-z0-9]{1,10}$/i

/**
 * @description 从文件名、后缀或 URL 中提取规范化后缀。
 * @param {unknown} value - 文件名、后缀或 URL
 * @returns {string} 不含点号的小写后缀
 */
export function getFileExtension(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''

  const cleanValue = raw.split('?')[0]?.split('#')[0] || ''
  const fileName = cleanValue.split('/').pop() || ''
  const extension = fileName.includes('.') ? fileName.split('.').pop() : fileName
  const normalized = String(extension || '')
    .trim()
    .toLowerCase()
  return FILE_EXTENSION_REGEXP.test(normalized) ? normalized : ''
}

/**
 * @description 根据文件名、后缀或 URL 获取文件类型。
 * @param {unknown} value - 文件名、后缀或 URL
 * @returns {string} 文件类型
 */
export function getFileType(value) {
  const normalizedValue = String(value ?? '')
    .trim()
    .toLowerCase()
  if (!normalizedValue) return FILE_TYPE.UNKNOWN
  if (KNOWN_FILE_TYPES.has(normalizedValue)) return normalizedValue

  const mimeCategory = normalizedValue.split('/')[0]
  if (KNOWN_FILE_TYPES.has(mimeCategory)) return mimeCategory

  const extension = getFileExtension(normalizedValue)
  const type = Object.keys(FILE_EXTS).find((key) => FILE_EXTS[key].includes(extension))
  return type || FILE_TYPE.UNKNOWN
}

/**
 * @description 从接口文件记录中解析文件类型，显式类型优先，文件名和 URL 兜底。
 * @param {Object|string} source - 文件记录或文件名/URL
 * @returns {string} 文件类型
 */
export function resolveFileType(source) {
  if (typeof source === 'string') return getFileType(source)

  const contentForm = String(source?.contentForm || '')
    .trim()
    .toUpperCase()
  if (contentForm === 'VIDEO') return FILE_TYPE.VIDEO
  if (contentForm === 'IMAGE') return FILE_TYPE.IMAGE

  const explicitType = getFileType(source?.fileType || source?.type || '')
  if (explicitType !== FILE_TYPE.UNKNOWN) return explicitType

  const candidates = [
    source?.fileName,
    source?.name,
    source?.fileUrl,
    source?.filePath,
    source?.downloadUrl,
    source?.url,
  ]
  for (const candidate of candidates) {
    const type = getFileType(candidate)
    if (type !== FILE_TYPE.UNKNOWN) return type
  }

  const previewType = getFileType(source?.previewType || source?.filePreviewType || '')
  if (previewType !== FILE_TYPE.UNKNOWN) return previewType

  return FILE_TYPE.UNKNOWN
}

/**
 * @description 根据文件名、后缀、URL 或文件记录获取对应图标路径。
 * @param {Object|string} source - 文件记录或文件名/URL
 * @returns {string} 图标 SVG 路径
 */
export function getFileIconPath(source) {
  const type = typeof source === 'object' ? resolveFileType(source) : getFileType(source)
  const iconMap = {
    [FILE_TYPE.PDF]: '/images/icons/file-pdf.svg',
    [FILE_TYPE.ZIP]: '/images/icons/file-zip.svg',
    [FILE_TYPE.VIDEO]: '/images/icons/file-video.svg',
    [FILE_TYPE.WORD]: '/images/icons/file-word.svg',
    [FILE_TYPE.EXCEL]: '/images/icons/file-excel.svg',
    [FILE_TYPE.PPT]: '/images/icons/file-ppt.svg',
    [FILE_TYPE.TXT]: '/images/icons/file-txt.svg',
    [FILE_TYPE.AUDIO]: '/images/icons/file-audio.svg',
    [FILE_TYPE.IMAGE]: '/images/icons/file-image.svg',
    [FILE_TYPE.UNKNOWN]: '/images/icons/file-unknown.svg',
  }
  return iconMap[type] || iconMap[FILE_TYPE.UNKNOWN]
}

/**
 * @description 格式化文件大小。
 * @param {number|string} size - 文件大小（字节）
 * @param {{ compactKbMb?: boolean, compactSpacing?: boolean }} [options] - 单位和间距格式
 * @returns {string} 可读文件大小
 */
export function formatFileSize(size, options = {}) {
  if (size === undefined || size === null || size === '') return EMPTY_VALUE_PLACEHOLDER
  const bytes = Number(size)
  if (!Number.isFinite(bytes) || bytes < 0) return String(size)
  if (bytes === 0) return '0 B'

  const unitBase = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const unitIndex = Math.floor(Math.log(bytes) / Math.log(unitBase))
  let unit = sizes[unitIndex]
  if (options.compactKbMb && (unit === 'KB' || unit === 'MB')) {
    unit = unit.slice(0, -1)
  }

  const separator = options.compactSpacing ? '' : ' '
  return `${parseFloat((bytes / Math.pow(unitBase, unitIndex)).toFixed(2))}${separator}${unit}`
}

/**
 * @description 解析资料大类，优先使用 TDC contentForm，缺失时按文件类型兜底。
 * @param {Object|string} source - 文件记录或文件名/URL
 * @returns {'video'|'image'|'document'|'other'} 资料大类
 */
export function resolveFileMaterialKind(source) {
  const contentForm = String(source?.contentForm || '')
    .trim()
    .toUpperCase()
  if (contentForm === 'VIDEO') return FILE_MATERIAL_KIND.VIDEO
  if (contentForm === 'IMAGE') return FILE_MATERIAL_KIND.IMAGE
  if (contentForm === 'DOCUMENT') return FILE_MATERIAL_KIND.DOCUMENT
  if (contentForm === 'CUSTOM') return FILE_MATERIAL_KIND.OTHER

  const fileType = resolveFileType(source)
  if (fileType === FILE_TYPE.VIDEO) return FILE_MATERIAL_KIND.VIDEO
  if (fileType === FILE_TYPE.IMAGE) return FILE_MATERIAL_KIND.IMAGE
  if (DOCUMENT_FILE_TYPES.has(fileType)) return FILE_MATERIAL_KIND.DOCUMENT
  return FILE_MATERIAL_KIND.OTHER
}

/**
 * @description 判断文件记录是否为视频。
 * @param {Object|string} source - 文件记录或文件名/URL
 * @returns {boolean} 是否为视频
 */
export function isVideoFile(source) {
  return resolveFileMaterialKind(source) === FILE_MATERIAL_KIND.VIDEO
}

/**
 * @description 判断文件是否允许下载；视频只允许在线预览，禁止下载。
 * @param {Object|string} source - 文件记录或文件名/URL
 * @returns {boolean} 是否允许下载
 */
export function canDownloadFile(source) {
  return !isVideoFile(source)
}

function resolveUpdateTimestamp(record) {
  const raw = record?.updateTime || record?.updatedAt || record?.updateDate || record?.createTime
  if (raw === null || raw === undefined || raw === '') return 0
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw < 1e12 ? raw * 1000 : raw
  const timestamp = Date.parse(String(raw))
  return Number.isFinite(timestamp) ? timestamp : 0
}

/**
 * @description 资料排序：视频 > 图片 > 文档 > 其它；同类型内按时间倒序。
 * @param {Array<Object>} records - 资料记录
 * @returns {Array<Object>} 排序后的新数组
 */
export function sortFileMaterials(records) {
  const list = Array.isArray(records) ? records : []
  return list
    .map((record, sourceIndex) => ({ record, sourceIndex }))
    .sort((left, right) => {
      const leftPriority = MATERIAL_PRIORITY[resolveFileMaterialKind(left.record)]
      const rightPriority = MATERIAL_PRIORITY[resolveFileMaterialKind(right.record)]
      if (leftPriority !== rightPriority) return leftPriority - rightPriority

      const timeDiff = resolveUpdateTimestamp(right.record) - resolveUpdateTimestamp(left.record)
      return timeDiff || left.sourceIndex - right.sourceIndex
    })
    .map(({ record }) => record)
}

const DOWNLOAD_FILE_EXTENSION_REGEXP = /\.[a-z0-9]{1,10}$/i
export const FILE_PREVIEW_OFFICE = 'office'

const OFFICE_FILE_TYPES = [FILE_TYPE.WORD, FILE_TYPE.EXCEL, FILE_TYPE.PPT]
const BROWSER_PREVIEW_FILE_TYPES = [
  FILE_TYPE.PDF,
  FILE_TYPE.IMAGE,
  FILE_TYPE.VIDEO,
  FILE_TYPE.AUDIO,
  FILE_TYPE.TXT,
]
const MEDIA_PREVIEW_PAGE_FILE_TYPES = [
  FILE_TYPE.IMAGE,
  FILE_TYPE.VIDEO,
  FILE_TYPE.AUDIO,
  FILE_TYPE.TXT,
]
const PREVIEW_FILE_TYPES = [
  ...OFFICE_FILE_TYPES,
  ...BROWSER_PREVIEW_FILE_TYPES,
  FILE_PREVIEW_OFFICE,
]

const withAppBasePath = (path) => {
  try {
    const { withBase } = useBasePath()
    return withBase(path)
  } catch {
    return path || ''
  }
}

/**
 * @description 规范化可展示文本，去除空白并统一转成字符串
 * @param {unknown} value - 原始值
 * @returns {string} 规范化后的文本
 */
const normalizeDownloadText = (value) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

/**
 * @description 从 URL 中提取默认下载文件名
 * @param {string} url - 文件地址
 * @returns {string} 文件名
 */
export const getDownloadFileName = (url) => {
  if (!url) return 'download'

  const normalizedUrl = String(url).split('?')[0]?.split('#')[0] || ''
  const rawName = normalizedUrl.split('/').pop() || 'download'

  try {
    return decodeURIComponent(rawName)
  } catch (error) {
    return rawName
  }
}

/**
 * @description 提取文件名中的后缀（包含点号），用于补齐标题下载名
 * @param {string} value - 文件名或文件地址
 * @returns {string} 文件后缀，如 `.pdf`
 */
const getDownloadFileExtension = (value) => {
  const normalizedValue = normalizeDownloadText(value)
  if (!normalizedValue) return ''

  const name = getDownloadFileName(normalizedValue)
  const match = name.match(DOWNLOAD_FILE_EXTENSION_REGEXP)
  return match ? match[0] : ''
}

const normalizePreviewType = (value) => {
  const rawType = normalizeDownloadText(value).toLowerCase()
  if (!rawType) return ''
  if (PREVIEW_FILE_TYPES.includes(rawType)) return rawType

  const typeFromExtension = getFileType(`preview.${rawType}`)
  return PREVIEW_FILE_TYPES.includes(typeFromExtension) ? typeFromExtension : ''
}

/**
 * @description Resolve the browser preview strategy from file metadata.
 * @param {{ url?: string, fileName?: string, fileType?: string }} source - File metadata.
 * @returns {string}
 */
export const resolveFilePreviewType = ({ url = '', fileName = '', fileType = '' } = {}) => {
  const explicitType = normalizePreviewType(fileType)
  const fallbackSource = String(fileName || url || '')
    .split('?')[0]
    .split('#')[0]
  const type = explicitType || getFileType(fallbackSource)

  if (OFFICE_FILE_TYPES.includes(type)) return FILE_PREVIEW_OFFICE
  return type
}

/**
 * @description Build the direct preview URL for browser-native and Office Viewer files.
 * @param {{ url?: string, fileName?: string, fileType?: string }} source - File metadata.
 * @returns {string}
 */
export const getFilePreviewUrl = ({ url = '', fileName = '', fileType = '' } = {}) => {
  if (!url) return ''

  const previewType = resolveFilePreviewType({ url, fileName, fileType })
  if (previewType === FILE_PREVIEW_OFFICE) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
  }

  if (BROWSER_PREVIEW_FILE_TYPES.includes(previewType)) {
    return url
  }

  return ''
}

/**
 * @description Build the URL opened in a new browser window for service file preview.
 * Browser-native image/media/text types are wrapped by a lightweight page to avoid OSS
 * response headers forcing download. Office files keep using Office Web Viewer.
 * @param {{ url?: string, fileName?: string, fileType?: string, poster?: string }} source - File metadata.
 * @returns {string}
 */
export const getFileOpenUrl = ({ url = '', fileName = '', fileType = '', poster = '' } = {}) => {
  if (!url) return ''

  const previewType = resolveFilePreviewType({ url, fileName, fileType })
  if (previewType === FILE_PREVIEW_OFFICE) {
    return getFilePreviewUrl({ url, fileName, fileType })
  }

  if (previewType === FILE_TYPE.PDF) {
    return url
  }

  if (MEDIA_PREVIEW_PAGE_FILE_TYPES.includes(previewType)) {
    const query = new URLSearchParams()
    query.set('url', previewType === FILE_TYPE.IMAGE ? getOssOriginUrl(url) : url)
    if (fileName) query.set('name', fileName)
    if (fileType) query.set('type', fileType)
    if (poster && previewType === FILE_TYPE.VIDEO) query.set('poster', poster)
    return withAppBasePath(`/website/service/media-preview?${query.toString()}`)
  }

  return ''
}

/**
 * @description 获取下载展示名称，优先使用业务标题，缺失时回退附件名称
 * @param {Object} source - 下载数据源
 * @param {string} [source.title] - 业务标题
 * @param {string} [source.name] - 附件名称
 * @param {string} [source.fileName] - 文件名称
 * @returns {string} 展示名称
 */
export const resolveDownloadDisplayName = (source = {}) => {
  const title = normalizeDownloadText(source?.title)
  if (title) return title

  const name = normalizeDownloadText(source?.name)
  if (name) return name

  const fileName = normalizeDownloadText(source?.fileName)
  if (fileName) return fileName

  return 'download'
}

/**
 * @description 获取最终下载文件名，优先使用标题作为文件基名，并补齐真实后缀
 * @param {Object} source - 下载数据源
 * @param {string} [source.title] - 业务标题
 * @param {string} [source.name] - 附件名称
 * @param {string} [source.fileName] - 文件名称
 * @param {string} [source.url] - 文件地址
 * @param {string} [source.fileUrl] - 文件地址
 * @param {string} [source.downloadUrl] - 文件地址
 * @returns {string} 最终下载文件名
 */
export const resolveDownloadFileName = (source = {}) => {
  const displayName = resolveDownloadDisplayName(source)
  if (DOWNLOAD_FILE_EXTENSION_REGEXP.test(displayName)) {
    return displayName
  }

  const fallbackName =
    normalizeDownloadText(source?.fileName) ||
    normalizeDownloadText(source?.name) ||
    getDownloadFileName(source?.fileUrl || source?.url || source?.downloadUrl || '')
  const extension =
    getDownloadFileExtension(fallbackName) ||
    getDownloadFileExtension(source?.fileUrl || source?.url || source?.downloadUrl || '')
  const normalizedBaseName = displayName.replace(/\.+$/, '') || 'download'

  return extension ? `${normalizedBaseName}${extension}` : normalizedBaseName
}

/**
 * @description 通过隐藏锚点触发浏览器下载
 * @param {Object} params - 下载参数
 * @param {string} params.href - 下载地址
 * @param {string} params.filename - 下载文件名
 * @param {boolean} [params.openInNewTab=false] - 是否在新标签页中打开
 * @returns {void}
 */
const triggerAnchorDownload = ({ href, filename, openInNewTab = false }) => {
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  link.rel = 'noopener noreferrer'

  if (openInNewTab) {
    link.target = '_blank'
  }

  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * @description 通过 XMLHttpRequest 拉取二进制内容，用于绕过浏览器对 PDF/图片等可预览资源的直接打开行为
 * @param {string} url - 文件地址
 * @returns {Promise<Blob>}
 */
const requestBlobByXhr = (url) => {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('GET', url, true)
    request.responseType = 'blob'

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        const blob = request.response
        if (!(blob instanceof Blob) || blob.size === 0) {
          reject(new Error('download file is empty'))
          return
        }
        resolve(blob)
        return
      }

      reject(new Error(`download request failed: ${request.status}`))
    }

    request.onerror = () => {
      reject(new Error('download request failed'))
    }

    request.onabort = () => {
      reject(new Error('download request aborted'))
    }

    request.send()
  })
}

/**
 * @description 判断当前文件是否需要通过 Blob 强制下载，避免浏览器直接预览
 * @param {Object} source - 下载资源信息
 * @param {string} source.filename - 下载文件名
 * @param {string} source.url - 文件地址
 * @param {string} [source.fileType] - 接口文件类型
 * @param {string} [source.contentForm] - TDC 内容形态
 * @returns {boolean}
 */
const shouldUseBlobDownload = ({ filename, url, fileType, contentForm }) => {
  const resolvedFileType = resolveFileType({
    fileName: filename,
    fileUrl: url,
    fileType,
    contentForm,
  })
  return BROWSER_PREVIEW_FILE_TYPES.includes(resolvedFileType)
}

/**
 * @description 下载远程文件；浏览器可预览类型走 Blob 强制下载，其余文件交给浏览器原生下载器展示进度
 * @param {Object} params - 下载参数
 * @param {string} params.url - 文件地址（支持相对路径或完整 OSS 地址）
 * @param {string} [params.filename] - 下载文件名
 * @param {string} [params.fileType] - 接口文件类型
 * @param {string} [params.contentForm] - TDC 内容形态
 * @returns {Promise<{ strategy: 'blob' | 'native' }>}
 */
export const downloadFileByUrl = async ({ url, filename, fileType, contentForm }) => {
  if (!import.meta.client) return

  if (!canDownloadFile({ url, fileName: filename, fileType, contentForm })) {
    const error = new Error('video download is not supported')
    error.code = 'VIDEO_DOWNLOAD_NOT_ALLOWED'
    throw error
  }

  const targetUrl = getOssOriginUrl(url)
  if (!targetUrl) {
    throw new Error('download url is empty')
  }

  const resolvedFileName = filename || getDownloadFileName(targetUrl)
  if (
    shouldUseBlobDownload({
      filename: resolvedFileName,
      url: targetUrl,
      fileType,
      contentForm,
    })
  ) {
    try {
      const blob = await requestBlobByXhr(targetUrl)
      const blobUrl = URL.createObjectURL(blob)

      triggerAnchorDownload({
        href: blobUrl,
        filename: resolvedFileName,
      })

      window.setTimeout(() => {
        URL.revokeObjectURL(blobUrl)
      }, 1000)

      return { strategy: 'blob' }
    } catch (error) {
      console.warn('blob download failed, fallback to native download:', error)
    }
  }

  triggerAnchorDownload({
    href: targetUrl,
    filename: resolvedFileName,
  })

  return { strategy: 'native' }
}
